import React from 'react'
import { Crown } from 'app/assets/icons'
import { media, style } from 'app/assets/styles'
import { LevelIndicator } from 'app/common/level-indicator'
import { Link } from 'app/common/link'
import { fonts, H4, SmallText, Text } from 'app/common/typography'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import { colors } from 'shared/assets/styles'
import styled from 'styled-components'
import type { KingdomPoints } from 'shared/types'
import { featureCodes, formatDateToDisplay } from 'shared/utils'
import { COLOR } from 'app-v2/design-tokens'
import { isMobile } from 'react-device-detect'
import { useSelector } from 'react-redux'
import type { RootState } from 'shared/store/reducers'

const { yellow, veryDarkGrey, greyBlue } = colors

type Props = {
    small?: boolean,
    pointInfos: KingdomPoints,
    fromEcommerce?: boolean,
    intl: IntlShape
}

const CrownCount = injectIntl((props: Props) => {
    const { small = false, pointInfos, fromEcommerce, intl } = props
    const { currentLevelIndex, expirationDate, nbLevels, points } = pointInfos
    const routeKingdom = intl.formatMessage({ id: 'routes.kingdom.dashboard' })
    const { available } = useSelector((state: RootState) => ({
        available: state.featuresState.available
    }))

    const kingScratchEnabled = available.includes(featureCodes.LOTTERY_SCRATCH)

    /* Adjust size of progress indicator to filled properly int the content */
    let adjust = 83
    if (points >= 10) {
        adjust -= 23
        if (points >= 100) {
            adjust -= 23
            if (points >= 1000) {
                adjust -= 23
            }
        }
    }
    return (
        <Content className={small && 'small'}>
            {!small && (
                <FormattedMessage id="kingdom.points.count.title">
                    {(text: string) => (
                        <H4 color="white" center>
                            {text}
                        </H4>
                    )}
                </FormattedMessage>
            )}
            <Infos>
                <PointsContainer>
                    <PointsCount color={yellow} font={fonts.flameRegular}>
                        {points}
                    </PointsCount>
                    <CrownContainer>
                        <Crown color={yellow} size={35} />
                    </CrownContainer>
                </PointsContainer>
                <ProgressIndicatorContainer adjust={adjust}>
                    <LevelIndicator
                        nbLevels={nbLevels}
                        currentLevelIndex={currentLevelIndex}
                    />
                </ProgressIndicatorContainer>
            </Infos>
            {!!expirationDate && (
                <ExpirationDateContainer>
                    <FormattedMessage
                        id={
                            kingScratchEnabled
                                ? 'kingdom.points.expiration.date.with-scratch'
                                : 'kingdom.points.expiration.date'
                        }
                        values={{
                            expirationDate: formatDateToDisplay(
                                new Date(expirationDate)
                            )
                        }}>
                        {(text: string) => (
                            <StyledSmallText color="white" right>
                                {text}
                            </StyledSmallText>
                        )}
                    </FormattedMessage>
                </ExpirationDateContainer>
            )}
            {fromEcommerce ? (
                <FormattedMessage id="kingdom.points.count.button">
                    {(text: string) => (
                        <StyledFakeButton border="none">
                            {text}
                        </StyledFakeButton>
                    )}
                </FormattedMessage>
            ) : (
                !small && (
                    <FormattedMessage id="kingdom.points.count.button">
                        {(text: string) => (
                            <StyledButtonMedium
                                url={`${routeKingdom}`}
                                border="none">
                                {text}
                            </StyledButtonMedium>
                        )}
                    </FormattedMessage>
                )
            )}
        </Content>
    )
})

const Content = styled.div`
    position: relative;
    overflow: hidden;
    padding: 15px;
    border-radius: 45px;
    box-sizing: border-box;
    background: ${COLOR.PRIMARY};
    background-repeat: no-repeat;
    background-size: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    min-height: 160px;
    ${media.medium`
        min-height: 200px;
    `};
    &.small {
        min-height: unset;
    }
`

const Infos = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`

const ExpirationDateContainer = styled.div`
    position: absolute;
    right: 20px;
    bottom: 0;
`

const StyledSmallText = styled(SmallText)`
    margin-right: 10px;
    font-size: ${isMobile ? '10px' : 'inherit'};
`

const PointsContainer = styled.div`
    display: flex;
`

const CrownContainer = styled.div`
    margin: 2px 10px 0 5px;
`

const PointsCount = styled(Text)`
    font-size: 36px;
    line-height: 39px;
`
const ProgressIndicatorContainer = styled.div`
    width: calc(100% - ${props => props.adjust}px);
    z-index: 1;
`

const StyledButtonMedium = styled(Link)`
    ${style.buttonMedium};
    background-color: ${yellow};
    z-index: 1;
`

const StyledFakeButton = styled.div`
    ${style.buttonMedium};
    background-color: ${yellow};
    z-index: 1;
`

export { CrownCount }
